import * as React from 'react';
import { toTitleCase } from '@/utils/textUtils';

type PropsWithChildren = {
  children?: React.ReactNode;
  [key: string]: any;
};

/**
 * Recursively applies title case to text nodes in a React node
 */
const titleCaseChildren = (children: React.ReactNode): React.ReactNode => {
  if (typeof children === 'string') {
    return toTitleCase(children);
  }

  if (Array.isArray(children)) {
    return React.Children.map(children, (child, index) => {
      if (typeof child === 'string') {
        return toTitleCase(child);
      }
      if (React.isValidElement(child)) {
        // Use type assertion to handle the spread of props
        const element = child as React.ReactElement<PropsWithChildren>;
        return React.cloneElement(element, {
          ...element.props,
          children: titleCaseChildren(element.props.children),
          key: index
        });
      }
      return child;
    });
  }

  if (React.isValidElement(children)) {
    const element = children as React.ReactElement<PropsWithChildren>;
    return React.cloneElement(element, {
      ...element.props,
      children: titleCaseChildren(element.props.children)
    });
  }

  return children;
};

type TitleCaseProps = {
  children: React.ReactNode;
  as?: keyof JSX.IntrinsicElements | React.ComponentType<any>;
  className?: string;
  [key: string]: any;
};

/**
 * A component that renders its children with title case text
 * @example
 * <TitleCase as="h1">this is a title</TitleCase>
 * // Renders: <h1>This Is A Title</h1>
 */
const TitleCase: React.FC<TitleCaseProps> = ({
  children,
  as: Component = 'span',
  className = '',
  ...props
}) => {
  return (
    <Component className={className} {...props}>
      {titleCaseChildren(children)}
    </Component>
  );
};

/**
 * Helper components for common text elements with title case applied
 */

type TitleCaseComponentProps = Omit<TitleCaseProps, 'as'> & {
  children: React.ReactNode;
};

export const TitleCaseH1: React.FC<TitleCaseComponentProps> = ({ children, ...props }) => (
  <TitleCase as="h1" className="text-3xl font-bold mb-4" {...props}>
    {children}
  </TitleCase>
);

export const TitleCaseH2: React.FC<TitleCaseComponentProps> = ({ children, ...props }) => (
  <TitleCase as="h2" className="text-2xl font-bold mb-3" {...props}>
    {children}
  </TitleCase>
);

export const TitleCaseH3: React.FC<TitleCaseComponentProps> = ({ children, ...props }) => (
  <TitleCase as="h3" className="text-xl font-semibold mb-2" {...props}>
    {children}
  </TitleCase>
);

export const TitleCaseP: React.FC<TitleCaseComponentProps> = ({ children, ...props }) => (
  <TitleCase as="p" className="mb-4 leading-relaxed" {...props}>
    {children}
  </TitleCase>
);

export const TitleCaseSpan: React.FC<TitleCaseComponentProps> = ({ children, ...props }) => (
  <TitleCase as="span" {...props}>
    {children}
  </TitleCase>
);

export const TitleCaseButton: React.FC<React.ButtonHTMLAttributes<HTMLButtonElement>> = ({
  children,
  ...props
}) => (
  <button {...props}>
    <TitleCase>{children}</TitleCase>
  </button>
);

export default TitleCase;
